import { AsyncPipe } from "@angular/common";
import { Component, EventEmitter, Input, OnInit, Output } from "@angular/core";
import {
  FormsModule,
  ReactiveFormsModule,
  UntypedFormBuilder,
  Validators,
} from "@angular/forms";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { firstValueFrom } from "rxjs";
import { map } from "rxjs/operators";
import { UserService } from "src/app/core/auth";
import { Day } from "src/app/core/day.model";
import { ButtonComponent } from "src/app/core/elements/button.component";
import { FormComponent } from "src/app/core/form.component";
import { DatePickerComponent } from "src/app/core/forms/date-picker.component";
import { PhoneNumberInputComponent } from "src/app/core/forms/phone-number-input.component";
import { TextareaComponent } from "src/app/core/forms/textarea.component";
import { TextboxComponent } from "src/app/core/forms/textbox.component";
import { TimepickerComponent } from "src/app/core/forms/timepicker.component";
import { SitesService } from "src/app/core/sites";
import { TimeOfDay } from "src/app/core/time-of-day.model";
import { AppointmentReference } from "src/app/partner/appointments/base-appointment.model";
import {
  HelpAssistEventType,
  HelpAssistTicketType,
} from "src/app/partner/help-assist/help-assist-enums";

import { HelpAssistService } from "src/app/partner/help-assist/help-assist.service";
import { HelpAssistTicketReference } from "src/app/partner/help-assist/models/ticket/base-help-assist-ticket.model";
import { HelpAssistTicketCreate } from "src/app/partner/help-assist/models/ticket/help-assist-ticket-create.model";
import { HelpAssistTicketAppointmentDraftUpdate } from "src/app/partner/help-assist/models/ticket-appointment-draft/help-assist-ticket-appointment-draft-update.model";
import { HelpAssistTicketScheduleUpdate } from "src/app/partner/help-assist/models/ticket-schedule/help-assist-ticket-appointment-schedule-update.model";
import {
  emailValidator,
  minuteStepValidator,
  phoneNumberValidator,
  pluckDistinctUntilChanged,
} from "src/utils";

interface FormValue {
  additionalComments: string;
  idealAppointmentDate: Day;
  idealStartTime: TimeOfDay;
  emailAddress: string;
  phoneNumber: string;
}

@UntilDestroy()
@Component({
  selector: "mr-scheduling-ticket-form",
  templateUrl: "./scheduling-ticket-form.component.html",
  styleUrls: ["./scheduling-ticket-form.component.scss"],
  imports: [
    FormsModule,
    ReactiveFormsModule,
    DatePickerComponent,
    TimepickerComponent,
    TextboxComponent,
    PhoneNumberInputComponent,
    TextareaComponent,
    ButtonComponent,
    AsyncPipe,
  ],
})
export class HelpAssistSchedulingTicketFormComponent
  extends FormComponent<FormValue>
  implements OnInit
{
  public constructor(
    formBuilder: UntypedFormBuilder,
    private readonly sites: SitesService,
    private readonly helpAssist: HelpAssistService,
    user: UserService,
  ) {
    super(formBuilder, {
      additionalComments: [null, Validators.maxLength(1000)],
      emailAddress: [
        user.details.emailAddress,
        [Validators.required, emailValidator],
      ],
      idealAppointmentDate: [null],
      idealStartTime: [null],
      phoneNumber: [null, phoneNumberValidator],
    });
  }

  @Input()
  public appointment: HelpAssistTicketAppointmentDraftUpdate | null = null;

  @Input() public appointmentId: AppointmentReference["id"] | null = null;

  @Output() public readonly submitSuccess =
    new EventEmitter<HelpAssistTicketReference>();
  @Output() public readonly submitError = new EventEmitter<unknown>();

  public readonly selectedSiteChanges = this.sites.selectedChanges;

  public readonly idealStartTimeMinuteStepChanges =
    this.selectedSiteChanges.pipe(
      pluckDistinctUntilChanged("appointmentInterval"),
    );

  public readonly minimumDayChanges = this.selectedSiteChanges.pipe(
    map((site) => Day.getTodayIn(site.timeZone)),
  );

  public ngOnInit(): void {
    this.reconfigure("idealAppointmentDate", {
      value: this.appointment?.idealAppointmentDate,
    });

    this.idealStartTimeMinuteStepChanges
      .pipe(untilDestroyed(this))
      .subscribe((step) => {
        this.reconfigure("idealStartTime", {
          validators: minuteStepValidator(step),
        });
      });
  }

  private async createRequest(
    formValue: FormValue,
  ): Promise<HelpAssistTicketCreate> {
    const site = await firstValueFrom(this.sites.selectedChanges);

    return new HelpAssistTicketCreate({
      schedule: new HelpAssistTicketScheduleUpdate({
        appointmentId: this.appointmentId,
        site: this.appointment?.site ?? site,
        ticketAppointment: this.appointment
          ? new HelpAssistTicketAppointmentDraftUpdate({
              ...this.appointment,
              idealAppointmentDate: formValue.idealAppointmentDate,
              idealStartTime: formValue.idealStartTime,
            })
          : null,
      }),
      emailAddress: formValue.emailAddress,
      events: formValue.additionalComments
        ? [
            {
              comment: formValue.additionalComments,
              type: HelpAssistEventType.Commented,
            },
          ]
        : [],
      orders: null,
      phoneNumber: formValue.phoneNumber,
      site: this.appointment?.site ?? site,
      type: HelpAssistTicketType.Scheduling,
    });
  }

  public async submit(): Promise<void> {
    try {
      const successResponse = await this.runSubmit(async (formValue) => {
        const request = await this.createRequest(formValue);

        return this.helpAssist.create(request);
      });

      if (successResponse) {
        this.submitSuccess.emit(successResponse.value);
      }
    } catch (error) {
      this.submitError.emit(error);
    }
  }
}
